import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router";
import { BadgeCheck, Eye, EyeOff, IdCard, Lock, Mail, Phone } from "lucide-react";
import { signupNurse } from "../api/signupClient";
import { AuthScreen } from "../components/AuthScreen";

export function Register() {
  const [email, setEmail] = useState("");
  const [npi, setNpi] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ username: string } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      const res = await signupNurse({
        email,
        npi,
        password,
        phone: phone || undefined,
      });
      setResult({ username: res.username });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <AuthScreen
        bannerTitle={
          <>
            <span className="block text-white">Verified intake,</span>
            <span className="block text-[#ff0d6e]"> cleaner routing,</span>
            <span className="block text-white"> faster signatures.</span>
          </>
        }
      >
        <div className="w-full flex flex-col items-center text-center gap-5">
          <span className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-200 shadow-xs">
            <BadgeCheck size={32} aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight font-display">
              Signup received
            </h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              Your NPI was verified and your account was created with reference ID{" "}
              <span className="font-mono font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded-md">
                {result.username}
              </span>
              .
            </p>
            <p className="text-xs text-slate-400 mt-2">
              An administrator will review your account before it can sign in.
            </p>
          </div>
          <Link
            className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] rounded-xl transition-all shadow-sm"
            to="/login"
          >
            Sign in
          </Link>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      bannerTitle={
        <>
          <span className="block text-white">Start intake,</span>
          <span className="block text-[#ff0d6e]"> route by NPI,</span>
          <span className="block text-white"> track every decision.</span>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight font-display">
            Create your account
          </h1>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            Register with your National Provider Identifier. New accounts require admin approval.
          </p>
        </div>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Email</span>
          <div className="relative flex items-center min-h-[44px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Mail size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              autoFocus
              required
            />
          </div>
        </label>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>10-Digit NPI</span>
          <div className="relative flex items-center min-h-[44px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <IdCard size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type="text"
              inputMode="numeric"
              placeholder="10-digit NPI"
              value={npi}
              onChange={(e) => setNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
              pattern="\d{10}"
              maxLength={10}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              required
            />
          </div>
        </label>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Phone</span>
          <div className="relative flex items-center min-h-[44px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Phone size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
          </div>
        </label>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Password</span>
          <div className="relative flex items-center min-h-[44px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Lock size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="p-1 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>
        </label>

        <label className="flex flex-col gap-1.5 text-xs font-bold text-slate-700">
          <span>Confirm password</span>
          <div className="relative flex items-center min-h-[44px] px-3.5 border border-slate-300 rounded-xl focus-within:border-[#012699] focus-within:ring-3 focus-within:ring-[#012699]/15 transition-all bg-white">
            <Lock size={18} className="text-slate-400 shrink-0 mr-2.5" aria-hidden="true" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((value) => !value)}
              className="p-1 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
            </button>
          </div>
        </label>

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || npi.length !== 10}
          className="w-full min-h-[46px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm cursor-pointer mt-1"
        >
          {submitting ? "Verifying NPI..." : "Create account"}
        </button>

        <p className="text-center text-xs text-slate-500 mt-1">
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-[#012699] hover:underline underline-offset-4">
            Sign in
          </Link>
        </p>
      </form>
    </AuthScreen>
  );
}
